import { Layer, PickingInfo } from '@deck.gl/core';
import { DataFilterExtension, DataFilterExtensionProps } from '@deck.gl/extensions';
import { PathLayer, SolidPolygonLayer } from '@deck.gl/layers';
import { parseColor255 } from '@fe/core';
import { CustomGeojson, CustomGeojsonDefaults, FeatureCollectionWithProperties, FeMapPlugin } from '@fe/maplibre-extensions';
import DOMPurify from 'dompurify';
import { Feature, LineString, MultiLineString, MultiPolygon, Polygon, Position } from 'geojson';
import { Map as MaplibreMap, Popup } from 'maplibre-gl';
import { DeckOverlayPlugin } from '../deck-overlay/deck-overlay-plugin';
import { splitFeaturesByElevation } from './elevation-utils';

const GROUP_PREFIX = 'fe-custom-geojson-3d';

export interface CustomGeojson3dOptions {
    layerSlot?: string;
}

type PathInstance = { feature: Feature; path: Position[] };
type PolygonInstance = { feature: Feature; polygon: Position[][] };

function groupId(sourceId: string): string {
    return `${GROUP_PREFIX}-${sourceId}`;
}

/**
 * Renders GeoJSON features that carry a 3rd (altitude, in meters) coordinate as true 3D geometry:
 *
 * Requires a `DeckOverlayPlugin` (`FlightLayerFacade.deckPlugin`) so elevated line/polygon layers share the
 * same MapboxOverlay instance (and its globe-mode rendering parameters) used by the rest of flight-layer.
 */
export class CustomGeojson3d implements FeMapPlugin {
    private readonly flat: CustomGeojson;
    private readonly deckOverlayPlugin: DeckOverlayPlugin;
    private readonly elevatedDataBySource = new Map<string, { features: Feature[]; collectionProperties: Record<string, unknown> }>();
    private readonly tooltip: Popup;
    private map: MaplibreMap | null = null;
    private hoveredHasTooltip = false;

    constructor(deckOverlayPlugin: DeckOverlayPlugin, options?: CustomGeojson3dOptions) {
        this.deckOverlayPlugin = deckOverlayPlugin;
        this.flat = new CustomGeojson(options?.layerSlot);
        this.tooltip = new Popup({
            closeButton: false,
            closeOnClick: false,
            maxWidth: '320px'
        });

        this.handleHover = this.handleHover.bind(this);
        this.getCursor = this.getCursor.bind(this);
        this.handleZoomEnd = this.handleZoomEnd.bind(this);
    }

    onAdd = (map: MaplibreMap): void => {
        this.map = map;
        this.flat.onAdd(map);
        this.map.on('zoomend', this.handleZoomEnd);
        this.deckOverlayPlugin.setPickingHandlers({
            onHover: this.handleHover,
            getCursor: this.getCursor
        });
    };

    onRemove = (): void => {
        this.flat.onRemove();

        for (const sourceId of Array.from(this.elevatedDataBySource.keys())) {
            this.deckOverlayPlugin.unregisterLayerGroup(groupId(sourceId));
        }
        this.elevatedDataBySource.clear();

        this.map?.off('zoomend', this.handleZoomEnd);
        this.deckOverlayPlugin.setPickingHandlers({});
        this.tooltip.remove();
        this.map = null;
    };

    addGeojson = (sourceId: string, geojson: FeatureCollectionWithProperties): void => {
        const { flat, elevated } = splitFeaturesByElevation(geojson.features as Feature[]);

        if (flat.length > 0) {
            this.flat.addGeojson(sourceId, { ...geojson, features: flat } as FeatureCollectionWithProperties);
        } else {
            this.flat.removeGeojson(sourceId);
        }

        if (elevated.length > 0) {
            this.elevatedDataBySource.set(sourceId, { features: elevated, collectionProperties: geojson.properties ?? {} });
            this.registerElevatedLayers(sourceId);
        } else if (this.elevatedDataBySource.has(sourceId)) {
            this.deckOverlayPlugin.unregisterLayerGroup(groupId(sourceId));
            this.elevatedDataBySource.delete(sourceId);
        }
    };

    removeGeojson = (sourceId: string): void => {
        this.flat.removeGeojson(sourceId);
        if (this.elevatedDataBySource.has(sourceId)) {
            this.deckOverlayPlugin.unregisterLayerGroup(groupId(sourceId));
            this.elevatedDataBySource.delete(sourceId);
        }
    };

    private handleZoomEnd(): void {
        for (const sourceId of this.elevatedDataBySource.keys()) {
            this.registerElevatedLayers(sourceId);
        }
    }

    private registerElevatedLayers(sourceId: string): void {
        const stored = this.elevatedDataBySource.get(sourceId);
        if (!stored) {
            return;
        }
        const layers = this.buildElevatedLayers(sourceId, stored.features, stored.collectionProperties);
        this.deckOverlayPlugin.registerLayerGroup(groupId(sourceId), layers);
    }

    private getCursor(params: { isDragging: boolean }): string {
        if (params.isDragging) {
            return 'grabbing';
        }
        return this.hoveredHasTooltip ? 'pointer' : 'grab';
    }

    private handleHover(info: PickingInfo): void {
        if (!this.map) {
            return;
        }

        const raw = info.object as { feature?: Feature } | Feature | undefined;
        const feature = (raw as { feature?: Feature })?.feature ?? (raw as Feature | undefined);
        const description = feature?.properties?.['description'];
        this.hoveredHasTooltip = !!description;

        if (!description || !info.coordinate) {
            this.tooltip.remove();
            return;
        }

        this.tooltip
            .setLngLat([info.coordinate[0], info.coordinate[1]])
            .setHTML(DOMPurify.sanitize(String(description)))
            .addTo(this.map);
    }

    private getStyle<T>(feature: Feature, collectionProperties: Record<string, unknown>, key: string, fallback: T): T {
        const featureValue = feature.properties?.[key];
        if (featureValue !== undefined && featureValue !== null) {
            return featureValue as T;
        }
        const collectionValue = collectionProperties[key];
        if (collectionValue !== undefined && collectionValue !== null) {
            return collectionValue as T;
        }
        return fallback;
    }

    private getColorWithOpacity(
        feature: Feature,
        collectionProperties: Record<string, unknown>,
        colorKey: string,
        opacityKey: string,
        colorDefault: string,
        opacityDefault: number
    ): [number, number, number, number] {
        const color = parseColor255(this.getStyle(feature, collectionProperties, colorKey, colorDefault));
        const opacity = this.getStyle(feature, collectionProperties, opacityKey, opacityDefault);
        color[3] = Math.round(color[3] * opacity);
        return color;
    }

    private getZoomFilterValue = (feature: Feature, collectionProperties: Record<string, unknown>): [number, number] => {
        const minzoom = Number(this.getStyle(feature, collectionProperties, 'minzoom', 0));
        const maxzoom = Number(this.getStyle(feature, collectionProperties, 'maxzoom', 24));
        return [minzoom, maxzoom];
    };

    private getZoomFilterRange(): [[number, number], [number, number]] {
        const zoom = this.map?.getZoom() ?? 0;
        return [
            [0, zoom],
            [zoom, 24]
        ];
    }

    private buildElevatedLayers(sourceId: string, features: Feature[], collectionProperties: Record<string, unknown>): Layer[] {
        const lines: Feature<LineString | MultiLineString>[] = [];
        const polygons: Feature<Polygon | MultiPolygon>[] = [];

        for (const feature of features) {
            const type = feature.geometry?.type;
            if (type === 'LineString' || type === 'MultiLineString') {
                lines.push(feature as Feature<LineString | MultiLineString>);
            } else if (type === 'Polygon' || type === 'MultiPolygon') {
                polygons.push(feature as Feature<Polygon | MultiPolygon>);
            }
        }

        const layers: Layer[] = [];

        if (lines.length > 0) {
            const pathInstances: PathInstance[] = lines.flatMap((feature) => {
                const paths = feature.geometry.type === 'LineString' ? [feature.geometry.coordinates] : feature.geometry.coordinates;
                return paths.map((path) => ({ feature, path }));
            });
            layers.push(this.buildLineLayer(sourceId, pathInstances, collectionProperties));
        }

        if (polygons.length > 0) {
            const polygonInstances: PolygonInstance[] = polygons.flatMap((feature) => {
                const rings = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates;
                return rings.map((polygon) => ({ feature, polygon }));
            });
            layers.push(this.buildPolygonLayer(sourceId, polygonInstances, collectionProperties));
        }

        return layers;
    }

    private buildLineLayer(sourceId: string, instances: PathInstance[], collectionProperties: Record<string, unknown>): Layer {
        return new PathLayer<PathInstance, DataFilterExtensionProps<PathInstance>>({
            id: `${groupId(sourceId)}-line`,
            data: instances,
            pickable: true,
            widthUnits: 'pixels',
            capRounded: true,
            jointRounded: true,
            billboard: true,
            getPath: (d) => d.path as [number, number, number][],
            getWidth: (d) => this.getStyle(d.feature, collectionProperties, 'line-width', CustomGeojsonDefaults['line-width']),
            getColor: (d) =>
                this.getColorWithOpacity(
                    d.feature,
                    collectionProperties,
                    'line-color',
                    'line-opacity',
                    CustomGeojsonDefaults['line-color'],
                    CustomGeojsonDefaults['line-opacity']
                ),
            getFilterValue: (d) => this.getZoomFilterValue(d.feature, collectionProperties),
            filterRange: this.getZoomFilterRange(),
            extensions: [new DataFilterExtension({ filterSize: 2 })],
            parameters: this.deckOverlayPlugin.getRenderingParams()
        });
    }

    private buildPolygonLayer(sourceId: string, instances: PolygonInstance[], collectionProperties: Record<string, unknown>): Layer {
        return new SolidPolygonLayer<PolygonInstance, DataFilterExtensionProps<PolygonInstance>>({
            id: `${groupId(sourceId)}-polygon`,
            data: instances,
            pickable: true,
            filled: true,
            extruded: false,
            wireframe: true,
            _full3d: true,
            getPolygon: (d) => d.polygon as [number, number, number][][],
            getFillColor: (d) =>
                this.getColorWithOpacity(
                    d.feature,
                    collectionProperties,
                    'fill-color',
                    'fill-opacity',
                    CustomGeojsonDefaults['fill-color'],
                    CustomGeojsonDefaults['fill-opacity']
                ),
            getLineColor: (d) =>
                this.getColorWithOpacity(
                    d.feature,
                    collectionProperties,
                    'line-color',
                    'line-opacity',
                    CustomGeojsonDefaults['line-color'],
                    CustomGeojsonDefaults['line-opacity']
                ),
            getFilterValue: (d) => this.getZoomFilterValue(d.feature, collectionProperties),
            filterRange: this.getZoomFilterRange(),
            extensions: [new DataFilterExtension({ filterSize: 2 })],
            parameters: this.deckOverlayPlugin.getRenderingParams()
        });
    }
}
